import type { LabStep } from '@/types/admin'

export interface PlanOutcome {
  /** Reviews from today until the card is mastered; null when the plan never gets there. */
  reviews: number | null
  /** Days from today to the mastering review. */
  days: number | null
  lapses: number
}

export function outcome(steps: LabStep[]): PlanOutcome {
  const index = steps.findIndex((step) => step.status === 'mastered')
  return {
    reviews: index === -1 ? null : index + 1,
    days: index === -1 ? null : Math.round(steps[index].day),
    lapses: steps.filter((step) => step.rating === 1).length,
  }
}

export interface Comparison {
  candidate: PlanOutcome
  saved: PlanOutcome
  /** Candidate minus saved; negative is sooner. Null when either never masters. */
  reviewDelta: number | null
  dayDelta: number | null
}

export function compare(steps: LabStep[], baseline: LabStep[]): Comparison {
  const candidate = outcome(steps)
  const saved = outcome(baseline)
  const delta = (a: number | null, b: number | null) => (a === null || b === null ? null : a - b)
  return {
    candidate,
    saved,
    reviewDelta: delta(candidate.reviews, saved.reviews),
    dayDelta: delta(candidate.days, saved.days),
  }
}

/** "3 fewer reviews", "same as saved" — the delta said in words, never by colour alone. */
export function deltaText(delta: number | null, unit: string) {
  if (delta === null) return 'no comparison'
  if (delta === 0) return 'same as saved'
  const n = Math.abs(delta)
  return `${n} ${unit}${n === 1 ? '' : 's'} ${delta < 0 ? 'sooner' : 'later'}`
}
